import { graphql, readInlineData } from 'react-relay';

import {
    trackEvent,
    eventNameConstants,
    stepInteractionConstants,
    TrackingObject,
} from 'dibs-tracking';

const { EVENT_PRODUCT_INTERACTION } = eventNameConstants;
const { STEP_INTERACTION_DISPLAY, STEP_INTERACTION_LINK_CLICK } = stepInteractionConstants;

import { pricingGuidanceTracking_item$key } from './__generated__/pricingGuidanceTracking_item.graphql';

export const getPriceStatus = ({
    item: itemRef,
}: {
    item: pricingGuidanceTracking_item$key;
}): string => {
    const item = readInlineData(
        graphql`
            fragment pricingGuidanceTracking_item on Item @inline {
                pricingGuidance(
                    isTrade: $isTrade
                    buyerId: $userId
                    userCountryCode: $userCountryCode
                    priceBookName: $priceBookName
                ) {
                    priceRange
                }
            }
        `,
        itemRef
    );

    switch (item?.pricingGuidance?.priceRange) {
        case 'OUT_OF_RANGE_LOW':
            return 'below min price';
        case 'LOWER_RANGE':
            return 'between min and ten percent less than median';
        case 'NEAR_MEDIAN_LOW':
            return 'ten percent less than median';
        case 'MEDIAN':
            return 'equal to median price';
        case 'NEAR_MEDIAN_HIGH':
            return 'ten percent more than median';
        case 'HIGHER_RANGE':
            return 'between ten percent more than median and max';
        case 'OUT_OF_RANGE_HIGH':
            return 'above max price';
        default:
            return 'other';
    }
};

const getBaseTrackingObject = ({
    item,
}: {
    item: pricingGuidanceTracking_item$key;
}): TrackingObject => {
    const step_type = `pdp - ${getPriceStatus({ item })}`;

    return {
        category: 'product',
        label: step_type,
        eventName: EVENT_PRODUCT_INTERACTION,
        step_type,
    };
};

export const trackPricingGuidanceModuleView = ({
    item,
    isEstimatedValueFurnitureTestVariant,
}: {
    item: pricingGuidanceTracking_item$key;
    isEstimatedValueFurnitureTestVariant: boolean;
}): void => {
    trackEvent({
        ...getBaseTrackingObject({ item }),
        action: 'recent sales module reached',
        noninteraction: 1,
        interaction_type: `${
            isEstimatedValueFurnitureTestVariant ? 'estimated value' : 'pricing guidance'
        } module`,
        step_interaction_name: STEP_INTERACTION_DISPLAY,
    });
};

export const trackPricingGuidanceModuleTooltipView = ({
    item,
    trigger,
    isEstimatedValueFurnitureTestVariant,
}: {
    item: pricingGuidanceTracking_item$key;
    trigger?: 'siderail';
    isEstimatedValueFurnitureTestVariant: boolean;
}): void => {
    trackEvent({
        ...getBaseTrackingObject({ item }),
        action: 'recent sales info icon clicked',
        noninteraction: 0,
        interaction_type: 'tooltip',
        step_interaction_name: STEP_INTERACTION_DISPLAY,
        trigger: `${
            isEstimatedValueFurnitureTestVariant ? 'estimated value' : 'pricing guidance'
        } - ${trigger || 'item details'}`,
    });
};

export const trackPricingGuidanceModuleBrowseSimilarLinkClick = ({
    item,
}: {
    item: pricingGuidanceTracking_item$key;
}): void => {
    const interaction_type = 'browse similar clicked';
    trackEvent({
        ...getBaseTrackingObject({ item }),
        action: interaction_type,
        noninteraction: 0,
        interaction_type,
        step_interaction_name: STEP_INTERACTION_LINK_CLICK,
    });
};

export const trackPricingGuidanceSimilarSoldClick = ({
    item,
}: {
    item: pricingGuidanceTracking_item$key;
}): void => {
    const interaction_type = 'similar sold items clicked';
    trackEvent({
        ...getBaseTrackingObject({ item }),
        action: interaction_type,
        noninteraction: 0,
        interaction_type,
        step_interaction_name: STEP_INTERACTION_LINK_CLICK,
    });
};
